import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  FileSignature, CheckCircle, Calendar, Download, Shield,
  AlertCircle, Award, HandCoins, Users
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const AffiliateContractPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [contractSigned, setContractSigned] = useState(user?.account_status === 'active');

  const handleSignContract = () => {
    if (!acceptedTerms) {
      toast.error('Veuillez accepter les conditions générales');
      return;
    }
    setContractSigned(true);
    toast.success('Contrat signé avec succès ! Votre espace Apporteur d\'affaires est maintenant actif.');
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        <div className="max-w-3xl mx-auto">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Contrat Apporteur d'Affaires</h1>
            <p className="text-slate-500">Programme de partenariat et commission</p>
          </div>

          {contractSigned ? (
            <Card className="border-0 shadow-sm border-t-4 border-t-emerald-500">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold mb-2" style={{ color: theme.colors?.primary }}>Contrat actif</h2>
                <p className="text-slate-500 mb-4">Votre contrat d'apporteur d'affaires est en vigueur</p>
                <Badge className="bg-emerald-100 text-emerald-700">Signé le 15/12/2024</Badge>
                
                <div className="mt-8 p-4 bg-slate-50 rounded-lg text-left">
                  <h3 className="font-semibold mb-3" style={{ color: theme.colors?.primary }}>Vos avantages</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Commission de 10% sur le premier projet de chaque client</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Conversion des gains en budget com' à 90€ HT/h</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Accès au tableau de bord affilié</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Lien de parrainage personnalisé</li>
                  </ul>
                </div>

                <Button variant="outline" className="mt-6">
                  <Download className="w-4 h-4 mr-2" /> Télécharger le contrat PDF
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Contract Terms */}
              <Card className="border-0 shadow-sm mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Conditions du programme
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <HandCoins className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="font-bold text-2xl text-emerald-600">10%</p>
                      <p className="text-sm text-slate-600">Commission</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="font-bold text-2xl text-blue-600">Illimité</p>
                      <p className="text-sm text-slate-600">Parrainages</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="font-bold text-2xl text-purple-600">90€</p>
                      <p className="text-sm text-slate-600">Tarif préférentiel/h</p>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg prose prose-sm max-h-64 overflow-y-auto">
                    <h4>Article 1 - Objet du contrat</h4>
                    <p>Le présent contrat définit les conditions du programme d'apporteur d'affaires entre l'Agence MSC et le Partenaire. Le Partenaire s'engage à recommander les services de l'Agence à son réseau professionnel.</p>
                    
                    <h4>Article 2 - Commissions</h4>
                    <p>Le Partenaire percevra une commission de 10% du montant HT du premier projet signé avec chaque nouveau client apporté. La commission est versée après encaissement complet du paiement client.</p>
                    
                    <h4>Article 3 - Avantages</h4>
                    <p>Le Partenaire bénéficie d'un tarif préférentiel de 90€ HT/heure (au lieu de 120€ HT) pour la conversion de ses commissions en prestations de communication.</p>
                    
                    <h4>Article 4 - Durée</h4>
                    <p>Le contrat est conclu pour une durée indéterminée. Chaque partie peut y mettre fin avec un préavis de 30 jours.</p>
                  </div>
                </CardContent>
              </Card>

              {/* Sign Contract */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-6">
                    <Checkbox 
                      id="terms" 
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                    />
                    <label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
                      J'ai lu et j'accepte les conditions générales du programme d'apporteur d'affaires. Je m'engage à respecter les règles de déontologie et à représenter l'agence de manière professionnelle.
                    </label>
                  </div>

                  <Button 
                    onClick={handleSignContract}
                    disabled={!acceptedTerms}
                    className="w-full"
                    style={{ backgroundColor: acceptedTerms ? theme.colors?.accent : undefined }}
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    Signer le contrat électroniquement
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AffiliateContractPage;